import BlockIcon from "@mui/icons-material/Block";
import ClearIcon from "@mui/icons-material/Clear";
import {
  Alert,
  Box,
  Typography,
  Drawer,
  TextField,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Button,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Snackbar,
  Tooltip,
} from "@mui/material";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AddCompanyDialog } from "@/components/email/components/AddCompanyDialog";
import { AddPersonDialog } from "@/components/email/components/AddPersonDialog";

import useDebounce from "../../hooks/useDebounce";
import { addNewCallsBlacklistsData } from "../../store/thunks/callsBlacklistThunk";
import {
  // addNewCompanyData,
  fetchCompanyList,
  updateCompany,
} from "../../store/thunks/companyListThunks";
import {
  // addNewPersonData,
  fetchPeopleListData,
  updatePeopleListData,
} from "../../store/thunks/peopleListThunks";
import PhoneUtils from "../../utils/PhoneUtils";

import {
  incomingCallNestedPopperSx,
  incomingCallPopperSx,
} from "./incomingCalls/overlayPolicy";

import type { AppDispatch, RootState } from "../../store/store";

/** Extract a human-readable message from a rejected thunk's unwrap() error. */
function extractErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const anyErr = error as any;
    return (
      anyErr.response?.data?.message ||
      anyErr.message ||
      fallback
    );
  }
  return fallback;
}

/** Utility: normalize phone (very small) */
function normalizePhone(raw?: string) {
  if (!raw) return "";
  return raw.replace(/[^\d+]/g, "");
}

type PhoneSlot = "phone" | "primaryPhone" | "secondaryPhone";
const PHONE_SLOT_ORDER: PhoneSlot[] = ["phone", "primaryPhone", "secondaryPhone"];

/**
 * Finds the first empty phone slot (Phone 1 -> Phone 2 -> Phone 3) on a contact,
 * or reports that the incoming number is already assigned to one of them.
 */
function getAvailablePhoneSlot(
  record: any,
  incomingPhone: string
): { slot: PhoneSlot | null; duplicate: boolean } {
  let firstEmpty: PhoneSlot | null = null;
  for (const slot of PHONE_SLOT_ORDER) {
    const rawValue = record?.[slot];
    const value = Array.isArray(rawValue) ? rawValue[0] : rawValue;
    const stringValue = value ? String(value).trim() : "";
    if (stringValue) {
      if (PhoneUtils.comparePhoneNumbers(stringValue, incomingPhone)) {
        return { slot: null, duplicate: true };
      }
    } else if (firstEmpty === null) {
      firstEmpty = slot;
    }
  }
  return { slot: firstEmpty, duplicate: false };
}

/** Helper to extract payload from dispatch result (supports RTK unwrap or payloads) */
function extractPayload(result: any) {
  if (!result) return null;
  if (result.payload !== undefined && Object.keys(result).length === 1)
    return result.payload;
  if (result.payload !== undefined) return result.payload;
  return result;
}

/** Drawer component */
type AssignDrawerProps = {
  open: boolean;
  phone?: string;
  email?: string;
  onClose: () => void;
  onSuccess?: (msg?: string) => void;
};

function AssignDrawer({ open, phone = "", email = "", onClose, onSuccess }: AssignDrawerProps) {
  const dispatch = useDispatch<AppDispatch>();

  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [results, setResults] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(false); // for assign/create/blacklist operations
  const [dataLoading, setDataLoading] = useState(false); // for initial combined data load
  const [searching, setSearching] = useState(false); // while user is typing / debounce waiting
  const { peopleList } = useSelector((state: RootState) => state.peopleList);
  const { companyList } = useSelector((state: RootState) => state.companyList);
  const [snack, setSnack] = useState<{
    msg: string;
    severity: "success" | "error" | "info";
  } | null>(null);
  const [createMenuAnchor, setCreateMenuAnchor] = useState<null | HTMLElement>(null);
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [confirmBlacklist, setConfirmBlacklist] = useState(false);

  const normalizedPhone = useMemo(() => normalizePhone(phone), [phone]);

  const combinedListRef = useRef<
    Array<{
      // original record fields...
      name: string;
      type: string;
      __source?: string;
      phone?: any;
      _phoneStrings?: string[]; // normalized phone strings used for fast matching
    }>
  >([]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  const MIN_PHONE_DIGITS = 3; // require at least 3 digits to search phone numbers
  const MIN_NAME_CHARS = 2; // require at least 2 chars to search names

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // keep debounceRef cleanup for safety if any remaining timers
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // useDebounce hook for name searches (keeps numeric immediate behavior)
  // We'll debounce the query string itself and react to the debounced value.
  // Import dynamically to avoid top-level import mismatch in patch
  // but we can import at module top in the next edit.

  useEffect(() => {
    // fetch initial lists once
    dispatch(fetchPeopleListData());
    dispatch(fetchCompanyList());
  }, [dispatch]);

  useEffect(() => {
    setDataLoading(true);
    try {
      const persons = extractPayload(peopleList) ?? [];
      const companies = extractPayload(companyList) ?? [];
      const companiesById = new Map(
        (Array.isArray(companies) ? companies : []).map((c: any) => [
          c.companyId ?? c.company_id,
          c,
        ])
      );
      const normalized = [
        ...(Array.isArray(persons)
          ? persons.map((p: any) => {
              const phonesArr: string[] =
                Array.isArray(p.phones) && p.phones.length
                  ? p.phones.map((ph: any) =>
                      String(ph ?? "").replace(/[^\d+]/g, "")
                    )
                  : p.phone
                  ? [String(p.phone).replace(/[^\d+]/g, "")]
                  : [];
              const companyName =
                p.company?.name ||
                companiesById.get(p.companyId ?? p.company_id)?.name ||
                "";
              return {
                ...p,
                __source: "person",
                type: "person",
                name: p.name || "",
                companyName,
                _phoneStrings: phonesArr,
              };
            })
          : []),
        ...(Array.isArray(companies)
          ? companies.map((c: any) => {
              const phonesArr: string[] =
                Array.isArray(c.phones) && c.phones.length
                  ? c.phones.map((ph: any) =>
                      String(ph ?? "").replace(/[^\d+]/g, "")
                    )
                  : c.phone
                  ? [String(c.phone).replace(/[^\d+]/g, "")]
                  : [];
              return {
                ...c,
                __source: "company",
                type: "company",
                name: c.name || "",
                _phoneStrings: phonesArr,
              };
            })
          : []),
      ];
      combinedListRef.current = normalized;
    } catch (e) {
      console.error("prepare combined list failed", e);
    } finally {
      if (isMountedRef.current) setDataLoading(false);
    }
  }, [peopleList, companyList]);

  // Debounced query for name/token searches. Numeric queries will be handled immediately.
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    // reset on close
    if (!open) {
      setQuery("");
      setResults([]);
      setSearching(false);
      return;
    }

    const trimmed = debouncedQuery.trim();

    if (
      trimmed === "" ||
      trimmed.length < Math.min(MIN_NAME_CHARS, MIN_PHONE_DIGITS)
    ) {
      setResults([]);
      setSearching(false);
      return;
    }

    // If numeric-only, handle immediately (no debounce)
    if (/^\+?\d+$/.test(trimmed)) {
      const qDigits = trimmed.replace(/[^\d+]/g, "");
      if (qDigits.length < MIN_PHONE_DIGITS) {
        setResults([]);
        setSearching(false);
        return;
      }
      const filteredImmediate = (combinedListRef.current || []).filter((r) =>
        (r._phoneStrings || []).some((p: string) => p.includes(qDigits))
      );
      setResults(filteredImmediate);
      setSearching(false);
      return;
    }

    // Name-based search using debounced query
    setSearching(true);
    const q = trimmed.toLowerCase();
    const qTokens = q.split(/\s+/).filter(Boolean);
    // If combined list still loading, do a short retry once
    if (dataLoading) {
      setTimeout(() => {
        if (!isMountedRef.current) return;
        const wordsMatch = (r: any) =>
          qTokens.length > 0 &&
          qTokens.every(
            (tok: string) =>
              tok.length >= MIN_NAME_CHARS &&
              ((r.name || "") as string)
                .toLowerCase()
                .split(/[^a-z0-9]+/)
                .filter(Boolean)
                .some((w: string) => w.startsWith(tok))
          );
        const filtered = (combinedListRef.current || []).filter(wordsMatch);
        setResults(filtered);
        setSearching(false);
      }, 150);
    } else {
      const wordsMatch = (r: any) =>
        qTokens.length > 0 &&
        qTokens.every(
          (tok: string) =>
            tok.length >= MIN_NAME_CHARS &&
            ((r.name || "") as string)
              .toLowerCase()
              .split(/[^a-z0-9]+/)
              .filter(Boolean)
              .some((w: string) => w.startsWith(tok))
        );
      const filtered = (combinedListRef.current || []).filter(wordsMatch);
      setResults(filtered);
      setSearching(false);
    }
  }, [debouncedQuery, open, dataLoading]);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  const handleAssign = async (record: any, slot: PhoneSlot = "phone") => {
    if (!normalizedPhone) {
      setSnack({ msg: "Invalid phone", severity: "error" });
      return;
    }
    setLoading(true);
    try {
      // Preserve every existing phone slot; only the target slot changes so a
      // partial update never nulls out the other numbers on the contact.
      const phoneFields: Record<PhoneSlot, string | null> = {
        phone: record?.phone ?? null,
        primaryPhone: record?.primaryPhone ?? null,
        secondaryPhone: record?.secondaryPhone ?? null,
      };
      phoneFields[slot] = normalizedPhone;

      if (record.type === "person") {
        const payload = {
          id: record?.personId,
          data: {
            personId: record?.personId,
            ...phoneFields,
          },
        };
        await (dispatch as any)(updatePeopleListData(payload)).unwrap();
        setSnack({
          msg: `Assigned ${normalizedPhone} to ${record.name}`,
          severity: "success",
        });
        onSuccess?.(`Assigned to ${record.name}`);
        onClose();
      } else {
        const payload = {
          data: {
            ...phoneFields,
            companyId: record?.companyId,
          },
          id: record?.companyId,
        };
        await (dispatch as any)(updateCompany(payload)).unwrap();
        setSnack({
          msg: `Assigned ${normalizedPhone} to ${record.name}`,
          severity: "success",
        });
        onSuccess?.(`Assigned to ${record.name}`);
        onClose();
      }
    } catch (error) {
      setSnack({
        msg: extractErrorMessage(error, "Assign failed"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAssignEmail = async (record: any) => {
    if (!email) {
      setSnack({ msg: "Invalid email", severity: "error" });
      return;
    }
    setLoading(true);
    try {
      if (record.type === "person") {
        const payload = {
          id: record?.personId,
          data: {
            personId: record?.personId,
            email: email,
          },
        };
        await (dispatch as any)(updatePeopleListData(payload)).unwrap();
        setSnack({
          msg: `Assigned ${email} to ${record.name}`,
          severity: "success",
        });
        onSuccess?.(`Assigned to ${record.name}`);
        onClose();
      } else {
        const payload = {
          data: {
            email: email,
            companyId: record?.companyId,
          },
          id: record?.companyId,
        };
        await (dispatch as any)(updateCompany(payload)).unwrap();
        setSnack({
          msg: `Assigned ${email} to ${record.name}`,
          severity: "success",
        });
        onSuccess?.(`Assigned to ${record.name}`);
        onClose();
      }
    } catch (error) {
      setSnack({
        msg: extractErrorMessage(error, "Assign failed"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // const handleCreate = async (type: "person" | "company", name: string) => {
  //   if (!name.trim()) {
  //     setSnack({ msg: "Name is required", severity: "error" });
  //     return;
  //   }
  //   if (!normalizedPhone) {
  //     setSnack({ msg: "Invalid phone", severity: "error" });
  //     return;
  //   }
  //   setLoading(true);
  //   try {
  //     if (type === "person") {
  //       const data = {
  //         email: "",
  //         name: name.trim(),
  //         phone: normalizedPhone,
  //         positionId: null,
  //         title: "",
  //         _model_key: Date.now(),
  //       };
  //       await (dispatch as any)(addNewPersonData({ data }));
  //       setSnack({ msg: `Created person "${name}"`, severity: "success" });
  //       onSuccess?.("Created person");
  //       setCreateDialog(null);
  //       onClose();
  //     } else {
  //       const data = {
  //         name: name.trim(),
  //         phone: normalizedPhone,
  //         _model_key: Date.now(),
  //       };
  //       await (dispatch as any)(addNewCompanyData({ data }));
  //       setSnack({ msg: `Created company "${name}"`, severity: "success" });
  //       onSuccess?.("Created company");
  //       setCreateDialog(null);
  //       onClose();
  //     }
  //   } catch (e) {
  //     setSnack({ msg: "Create failed", severity: "error" });
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleBlacklist = async () => {
    if (!normalizedPhone) {
      setSnack({ msg: "Invalid phone", severity: "error" });
      return;
    }
    setLoading(true);
    try {
      await (dispatch as any)(
        addNewCallsBlacklistsData({
          data: { phone_number: normalizedPhone },
        })
      ).unwrap();
      setSnack({
        msg: `${normalizedPhone} added to blacklist`,
        severity: "success",
      });
      setConfirmBlacklist(false);
      onSuccess?.("Blacklisted");
      onClose();
    } catch (error) {
      setSnack({
        msg: extractErrorMessage(error, "Blacklist failed"),
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCreateMenuAnchor(event.currentTarget);
  };
  const handleCreateMenuClose = () => setCreateMenuAnchor(null);
  const handleAddPerson = () => {
    handleCreateMenuClose();
    setAddPersonOpen(true);
  };
  const handleAddCompany = () => {
    handleCreateMenuClose();
    setAddCompanyOpen(true);
  };

  const handlePersonCreated = (createdPerson?: any) => {
    if (!createdPerson) return;
    const personId = createdPerson.person_id ?? createdPerson.personId;
    const companyName = createdPerson.company?.name || "";
    const row = {
      ...createdPerson,
      id: personId,
      personId,
      name: createdPerson.name || "",
      type: "person",
      __source: "person",
      companyName,
      phone: createdPerson.phone || normalizedPhone,
    };
    // Auto-assign the number/email to the newly created person and close
    // the drawer, returning straight to the incoming call view.
    if (phone) {
      handleAssign(row, "phone");
    } else if (email) {
      handleAssignEmail(row);
    }
  };

  const handleCompanyCreated = (createdCompany?: any) => {
    if (!createdCompany) return;
    const companyId = createdCompany.company_id ?? createdCompany.companyId;
    const row = {
      ...createdCompany,
      id: companyId,
      companyId,
      name: createdCompany.name || "",
      type: "company",
      __source: "company",
      phone: createdCompany.phone || normalizedPhone,
    };
    // Auto-assign the number/email to the newly created company and close
    // the drawer, returning straight to the incoming call view.
    if (phone) {
      handleAssign(row, "phone");
    } else if (email) {
      handleAssignEmail(row);
    }
  };

  const getOverlayContainer = () =>
    document.querySelector('[aria-label="Incoming calls"]') ?? document.body;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        onClick={(e) => e.stopPropagation()}
        container={getOverlayContainer}
      >
        <Box sx={{ width: 420, height: "100vh", p: 2, display: "flex", flexDirection: "column" }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="h6">Assign / Create</Typography>
          </Stack>
          {/* Show phone or email assignment UI based on prop */}
          {phone ? (
            <>
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={(theme) => ({
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                    whiteSpace: "nowrap",
                  })}
                >
                  Selected Phone:
                </Typography>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <Typography sx={{ color: (theme) => theme.palette.primary.main }}>
                    {PhoneUtils.renderer(phone)}
                  </Typography>
                </Box>
              </Box>
              <TextField
                size="small"
                fullWidth
                placeholder="Search people or companies or phone"
                value={query}
                inputRef={searchRef}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ mb: 2 }}
                autoFocus
                InputProps={{
                  endAdornment: query ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setQuery("");
                          setResults([]);
                          setSearching(false);
                          if (debounceRef.current) {
                            clearTimeout(debounceRef.current as any);
                            debounceRef.current = null;
                          }
                          setTimeout(() => searchRef.current?.focus(), 0);
                        }}
                        aria-label="clear"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
              {dataLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {searching && !dataLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                  <Typography variant="body2">Searching…</Typography>
                </Box>
              )}
              <List dense sx={{ flex: 1, overflow: "auto", mb: 2 }}>
                {loading && (
                  <ListItem>
                    <ListItemText primary="Working..." />
                  </ListItem>
                )}
                {!loading && !searching && results.length === 0 && query && (
                  <ListItem>
                    <ListItemText primary="No results" />
                  </ListItem>
                )}
                {!loading &&
                  !searching &&
                  results.map((r: any) => {
                    const { slot: availableSlot, duplicate } =
                      getAvailablePhoneSlot(r, normalizedPhone);
                    return (
                      <ListItem
                        key={r.id + (r.__source ?? r.type ?? "")}
                        secondaryAction={
                          <Stack direction="row" spacing={1} alignItems="center">
                            {availableSlot ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleAssign(r, availableSlot)}
                              >
                                Assign Number
                              </Button>
                            ) : duplicate ? (
                              <Typography variant="caption" color="text.secondary">
                                Already assigned
                              </Typography>
                            ) : null}
                          </Stack>
                        }
                      >
                        <ListItemText
                          primary={
                            <>
                              <span style={{ fontWeight: 600 }}>{r.name}</span>{" "}
                              <small style={{ marginLeft: 6, color: "#666" }}>
                                ({r.type ?? r.__source})
                              </small>
                            </>
                          }
                          secondary={
                            <>
                              {r.type === "person" && (
                                <>
                                  Company: {r.companyName || "No company"}
                                  <br />
                                </>
                              )}
                              {(() => {
                                const phoneRows = [
                                  { label: "Phone", value: r.phone },
                                  { label: "Phone 2", value: r.primaryPhone },
                                  { label: "Phone 3", value: r.secondaryPhone },
                                ].filter(({ value }) => value && String(value).trim());

                                if (!phoneRows.length) return "No phone";

                                const content = phoneRows.map(({ label, value }, idx) => (
                                  <Fragment key={label}>
                                    {`${label}: ${PhoneUtils.renderer(value)}`}
                                    {idx < phoneRows.length - 1 && <br />}
                                  </Fragment>
                                ));

                                if (!availableSlot && !duplicate) {
                                  return (
                                    <Tooltip title="No phone slot available">
                                      <span>{content}</span>
                                    </Tooltip>
                                  );
                                }
                                return content;
                              })()}
                            </>
                          }
                        />
                      </ListItem>
                    );
                  })}
              </List>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" spacing={1} mb={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleCreateMenuOpen}
                >
                  Create Person / Company
                </Button>
                <Button
                  startIcon={<BlockIcon />}
                  color="error"
                  variant="contained"
                  size="small"
                  onClick={() => setConfirmBlacklist(true)}
                >
                  Blacklist Number
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={(theme) => ({
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                    whiteSpace: "nowrap",
                  })}
                >
                  Selected Email:
                </Typography>
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  <Typography sx={{ color: (theme) => theme.palette.primary.main }}>
                    {email}
                  </Typography>
                </Box>
              </Box>
              <TextField
                size="small"
                fullWidth
                placeholder="Search people or companies"
                value={query}
                inputRef={searchRef}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ mb: 2 }}
                autoFocus
                InputProps={{
                  endAdornment: query ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setQuery("");
                          setResults([]);
                          setSearching(false);
                          if (debounceRef.current) {
                            clearTimeout(debounceRef.current as any);
                            debounceRef.current = null;
                          }
                          setTimeout(() => searchRef.current?.focus(), 0);
                        }}
                        aria-label="clear"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
              {dataLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
              {searching && !dataLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                  <Typography variant="body2">Searching…</Typography>
                </Box>
              )}
              <List dense sx={{ flex: 1, overflow: "auto", mb: 2 }}>
                {loading && (
                  <ListItem>
                    <ListItemText primary="Working..." />
                  </ListItem>
                )}
                {!loading && !searching && results.length === 0 && query && (
                  <ListItem>
                    <ListItemText primary="No results" />
                  </ListItem>
                )}
                {!loading &&
                  !searching &&
                  results.map((r: any) => {
                    const hasEmail =
                      (Array.isArray(r.email) && r.email.length > 0) ||
                      !!(r.email && String(r.email).trim());
                    return (
                      <ListItem
                        key={r.id + (r.__source ?? r.type ?? "")}
                        secondaryAction={
                          <Stack direction="row" spacing={1}>
                            {hasEmail === false ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleAssignEmail(r)}
                              >
                                Assign Email
                              </Button>
                            ) : null}
                          </Stack>
                        }
                      >
                        <ListItemText
                          primary={
                            <>
                              <span style={{ fontWeight: 600 }}>{r.name}</span>{" "}
                              <small style={{ marginLeft: 6, color: "#666" }}>
                                ({r.type ?? r.__source})
                              </small>
                            </>
                          }
                          secondary={
                            <>
                              {r.type === "person" && (
                                <>
                                  Company: {r.companyName || "No company"}
                                  <br />
                                </>
                              )}
                              {r.email ? `Email: ${r.email}` : "No email"}
                            </>
                          }
                        />
                      </ListItem>
                    );
                  })}
              </List>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleCreateMenuOpen}
                >
                  Create Person / Company
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Drawer>

      {/* Create menu */}
      <Menu
        anchorEl={createMenuAnchor}
        open={Boolean(createMenuAnchor)}
        onClose={handleCreateMenuClose}
        onClick={(e) => e.stopPropagation()}
        sx={incomingCallPopperSx}
      >
        <MenuItem onClick={handleAddPerson}>New Person</MenuItem>
        <MenuItem onClick={handleAddCompany}>New Company</MenuItem>
      </Menu>

      {/* Dialogs */}
      <AddPersonDialog
        open={addPersonOpen}
        onClose={() => setAddPersonOpen(false)}
        email={email || ""}
        phone={phone || ""}
        name=""
        onCreated={handlePersonCreated}
        sx={incomingCallPopperSx}
        popperSx={incomingCallNestedPopperSx}
      />
      <AddCompanyDialog
        open={addCompanyOpen}
        onClose={() => setAddCompanyOpen(false)}
        email={email || ""}
        phone={phone || ""}
        name=""
        onSaveSuccess={handleCompanyCreated}
        sx={incomingCallPopperSx}
        popperSx={incomingCallNestedPopperSx}
      />

      {/* Blacklist confirm */}
      <Dialog
        open={confirmBlacklist}
        onClose={() => setConfirmBlacklist(false)}
        onClick={(e) => e.stopPropagation()}
        sx={incomingCallPopperSx}
      >
        <DialogTitle>Add to blacklist</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to add <strong>{normalizedPhone}</strong> to
            the blacklist?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmBlacklist(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleBlacklist}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={Boolean(snack)}
        onClose={() => setSnack(null)}
        autoHideDuration={5000}
        sx={incomingCallPopperSx}
      >
        {snack ? (
          <Alert severity={snack.severity}>{snack.msg}</Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}

export default AssignDrawer;
